export interface ChartData {
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  change: number;
  changePercent: number;
  time: string;
  isUp: boolean;
}

export interface Trade {
  type: string;
  price: number;
  size: number;
  time: Date;
  profit?: number;
  // UI 표시용(서버 저장에는 사용하지 않음)
  reason?: string;
}

export interface LimitOrder {
  id: string;
  type: 'buy' | 'sell';
  price: number;
  size: number;
  time: Date;
  status: 'pending' | 'filled' | 'cancelled';
}

export interface GameState {
  currentPrice: number;
  history: ChartData[];
  playerBalance: number;
  playerPosition: 'long' | 'short' | null;
  positionSize: number;
  entryPrice: number | null;
  isGameActive: boolean;
  profit: number;
  stockQuantity: number;
  averagePrice: number;
  initialBalance: number;
  candleCount: number;
  feedback: {
    message: string;
    type: 'positive' | 'negative' | 'neutral' | 'tip';
    timestamp: number;
  } | null;
  tradingStats: {
    totalTrades: number;
    successfulTrades: number;
    averageProfit: number;
    bestTrade: number;
    worstTrade: number;
  };
  limitOrders: LimitOrder[];
}

export interface RankingEntry {
  rank: number;
  userKey: number;
  nickname: string;
  balance: number;
  profitRate: number;
  profitRateFormatted: string;
  gameCount: number;
  badge: {
    id: string;
    name: string;
    icon: string;
    color: string;
  } | null;
  selectedTitle: {
    titleId: number;
    name: string;
    icon: string;
    rarity: 'common' | 'rare' | 'epic' | 'legendary';
  } | null;
  updatedAt: string;
  isPublicProfile?: boolean; // 매매내역 공개 여부
}

export interface GameEndData {
  finalBalance: number;
  finalStockQuantity: number;
  finalCurrentPrice: number;
  finalCandleCount: number;
  finalTradeHistory: Trade[];
  startingBalance: number;
  lastCandle?: ChartData; // 게임 종료 시점의 마지막 캔들
  unlockedAchievements?: {
    unlockedTitles: Array<{ code: string; name: string; icon: string }>;
    unlockedAchievements: Array<{ code: string; name: string; icon: string }>;
  };
}

export interface StockInfo {
  stockCode: string;
  stockName: string;
  startDate: string; // 전체 데이터의 시작 날짜
  endDate: string; // 전체 데이터의 종료 날짜
  gameStartDate?: string; // 게임이 실제로 시작된 날짜 (gameData[0]의 날짜, 첫 번째 봉)
  gameEndDate?: string; // 게임이 진행될 수 있는 최대 기간의 마지막 날짜 (gameData의 마지막 봉, 50봉 뒤)
}

export const INITIAL_BALANCE = 10000000;

// 게임 최대 봉 개수 제한 (이 값을 변경하면 게임 기간이 변경됨)
export const MAX_CANDLE_COUNT = 50;

export const createInitialGameState = (balance: number): GameState => ({
  currentPrice: 50000,
  history: [],
  playerBalance: balance,
  playerPosition: null,
  positionSize: 0,
  entryPrice: null,
  isGameActive: false,
  profit: 0,
  stockQuantity: 0,
  averagePrice: 0,
  initialBalance: balance,
  candleCount: 0,
  feedback: null,
  tradingStats: {
    totalTrades: 0,
    successfulTrades: 0,
    averageProfit: 0,
    bestTrade: 0,
    worstTrade: 0,
  },
  limitOrders: [],
});
